import {
    useMemo,
    type FC
} from "react";
import {
    View
} from "react-native";
import type {
    DataTableCardsProps
} from "./type";
import stylesheet, {
    DATA_TABLE_CARD_SKELETON_WIDTHS,
    DATA_TABLE_CARD_SKELETON_COUNT,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useDataTableSelectionContext,
    useDataTableContext
} from "../../context";
import cardStylesheet, {
    useStyles as useCardStyles
} from "../card/stylesheet";
import {
    formatNumber
} from "../../../../helpers/locale";
import {
    getDataTableCardLayout
} from "../../util";
import Skeleton from "../../../skeleton";
import DataTableCard from "../card";
import Text from "../../../text";

const DataTableCards: FC<DataTableCardsProps> = ({
    customTheme: customThemeProp
}) => {
    const context = useDataTableContext({
        part: "Content"
    });

    const selectionContext = useDataTableSelectionContext();

    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const {
        container: containerDynamicStyle,
        summary: summaryDynamicStyle,
        list: listDynamicStyle
    } = useStyles({
        colors,
        spaces
    });

    const {
        surface: skeletonSurfaceDynamicStyle
    } = useCardStyles({
        isFocusVisible: false,
        isSelected: false,
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    });

    const columns = context?.columns;

    const layout = useMemo(() => columns ? getDataTableCardLayout({
        columns
    }) : undefined, [
        columns
    ]);

    const selectedSet = useMemo(() => new Set(selectionContext?.selectedRowIds), [
        selectionContext?.selectedRowIds
    ]);

    if(!context || !layout) {
        return null;
    }

    const currentTheme = customThemeProp ?? context.customTheme;

    const isSelectionMode = context.selectionBehaviour !== "none" && selectedSet.size > 0;

    const filterParts = context.allColumns.flatMap((column) => {
        const values = context.columnFilters[column.id] ?? [];

        if(values.length === 0) {
            return [];
        }

        return [
            `${column.header}: ${values.map((value) => column.filterOptions?.find((option) => option.value === value)?.label ?? value).join(", ")}`
        ];
    });

    const primaryRule = context.sorting[0];

    const primaryColumn = context.allColumns.find((column) => column.id === primaryRule?.id);

    const summary = [
        localize("data-table-record-count", [
            formatNumber({
                value: context.totalCount,
                locale: activeLocale
            })
        ]),
        ...filterParts
    ].join(" · ");

    const renderSkeletons = () => {
        return Array.from({
            length: DATA_TABLE_CARD_SKELETON_COUNT
        }, (_, cardIndex) => <View
            style={[
                cardStylesheet.surface,
                skeletonSurfaceDynamicStyle
            ]}
            key={cardIndex}
        >
            {
                DATA_TABLE_CARD_SKELETON_WIDTHS.map((width, lineIndex) => <Skeleton
                    typography={lineIndex === 0 ? "titleSmallSize" : "bodySmallSize"}
                    customTheme={currentTheme}
                    key={lineIndex}
                    variant="text"
                    width={width}
                />)
            }
        </View>);
    };

    return <View
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        <View
            style={[
                stylesheet.summary,
                summaryDynamicStyle
            ]}
        >
            <Text
                customTheme={currentTheme}
                variant="bodySmallSize"
                color="mid"
            >
                {summary}
            </Text>
            {
                primaryColumn && primaryRule ?
                    <Text
                        customTheme={currentTheme}
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {`${primaryColumn.header} ${primaryRule.direction === "asc" ? "↑" : "↓"}`}
                    </Text>
                :
                    null
            }
        </View>
        <View
            style={listDynamicStyle}
            role="list"
        >
            {
                context.isLoading ?
                    renderSkeletons()
                :
                    context.rows.map((row) => <DataTableCard
                        isSelected={selectedSet.has(row.id)}
                        isSelectionMode={isSelectionMode}
                        customTheme={currentTheme}
                        layout={layout}
                        key={row.id}
                        row={row}
                    />)
            }
        </View>
    </View>;
};
export default DataTableCards;
